import React from 'react';
import { Icon } from './Icons';

export default function Pagination({
  page = 0,
  totalPages = 1,
  totalElements = 0,
  size = 20,
  onPageChange,
  loading = false,
}) {
  if (totalElements === 0 || totalPages <= 1) {
    return null;
  }

  const currentPage = page + 1; // 1-indexed for display
  const startItem = Math.min(page * size + 1, totalElements);
  const endItem = Math.min((page + 1) * size, totalElements);

  // Generate page numbers with ellipsis
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      let start = Math.max(1, currentPage - 2);
      let end = Math.min(totalPages, currentPage + 2);

      if (currentPage <= 3) {
        start = 1;
        end = 4;
      } else if (currentPage >= totalPages - 2) {
        start = totalPages - 3;
        end = totalPages;
      }

      if (start > 1) {
        pages.push(1);
        if (start > 2) pages.push('ellipsis-start');
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (end < totalPages) {
        if (end < totalPages - 1) pages.push('ellipsis-end');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  return (
    <div className="pagination-container" aria-label="Pagination Navigation">
      <div className="pagination-info">
        Showing <span className="font-semibold text-primary">{startItem}</span> to{' '}
        <span className="font-semibold text-primary">{endItem}</span> of{' '}
        <span className="font-semibold text-primary">{totalElements}</span> notes
      </div>

      <div className="pagination-controls">
        <button
          type="button"
          className="pagination-btn nav-btn"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 0 || loading}
          title="Go to previous page"
          aria-label="Previous page"
        >
          <Icon name="chevron-left" size={16} />
          <span className="pagination-btn-text">Previous</span>
        </button>

        <div className="pagination-pages-group">
          {getPageNumbers().map((p, idx) => {
            if (typeof p === 'string') {
              return (
                <span key={`ellipsis-${idx}`} className="pagination-ellipsis">
                  …
                </span>
              );
            }
            const isActive = p === currentPage;
            return (
              <button
                key={p}
                type="button"
                className={`pagination-number-btn ${isActive ? 'active' : ''}`}
                onClick={() => onPageChange(p - 1)}
                disabled={loading}
                aria-current={isActive ? 'page' : undefined}
                aria-label={`Page ${p}`}
              >
                {p}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          className="pagination-btn nav-btn"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages - 1 || loading}
          title="Go to next page"
          aria-label="Next page"
        >
          <span className="pagination-btn-text">Next</span>
          <Icon name="chevron-right" size={16} />
        </button>
      </div>
    </div>
  );
}

